import {
  TicketCategory,
  SupportTicket,
  SupportTicketListItem,
  ChatStep,
  ChatMessage,
  ChatFormData,
} from 'Interfaces/SupportTickets/supportTicketInterfaces';
import { makeAutoObservable } from 'mobx';
import { toast } from 'react-toastify';
import { supportTicketServices, ComprehensiveTicketData } from 'services/supportTicketServices';
import { captureComprehensiveTicketData } from 'utils/captureTicketData';
import { v4 as uuidv4 } from 'uuid';

export class SupportTicketStore {
  // State
  categories: TicketCategory[] = [];
  myTickets: SupportTicketListItem[] = [];
  selectedTicket: SupportTicket | null = null;

  // Modal visibility
  isCreateModalVisible: boolean = false;
  isDetailModalVisible: boolean = false;

  // Chat UI state
  isChatOpen: boolean = false;
  chatStep: ChatStep = ChatStep.CLOSED;
  chatMessages: ChatMessage[] = [];
  isTypingIndicatorVisible: boolean = false;

  // Form data (persists across open/close)
  formData: ChatFormData = {
    selectedCategory: null,
    title: '',
    description: '',
    attachments: []
  };

  // Loading states
  isCategoriesLoading: boolean = false;
  isTicketsLoading: boolean = false;
  isTicketDetailLoading: boolean = false;
  isCreatingTicket: boolean = false;
  isSyncingStatus: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Fetch all ticket categories
   */
  fetchCategories = async () => {
    this.isCategoriesLoading = true;
    try {
      const data = await supportTicketServices.getCategories();
      this.categories = data.categories;
    } catch (error) {
      console.log('SupportTicketStore => fetchCategories => ', error);
      toast.error('Failed to load ticket categories');
    } finally {
      this.isCategoriesLoading = false;
    }
  };

  /**
   * Create a comprehensive JSM ticket with all available data
   */
  createJSMTicket = async (ticketData: ComprehensiveTicketData) => {
    this.isCreatingTicket = true;
    try {
      const data = await supportTicketServices.createJSMTicket(ticketData);

      // Show attachment status if any failed
      if (data.attachments && data.attachments.length > 0) {
        const failedAttachments = data.attachments.filter(a => a.status === 'failed');
        if (failedAttachments.length > 0) {
          toast.warning(
            `${failedAttachments.length} attachment(s) failed to upload. Please try adding them manually to the Jira ticket.`
          );
        }
      }

      // Refresh tickets list in background
      this.fetchMyTickets();

      return data;
    } catch (error) {
      console.log('SupportTicketStore => createJSMTicket => ', error);
      toast.error('Failed to create ticket. Please try again.');
      throw error;
    } finally {
      this.isCreatingTicket = false;
    }
  };

  /**
   * Create a new support ticket (legacy method, forwards to JSM)
   * @deprecated Use createJSMTicket instead for better data capture
   */
  createTicket = async (
    categoryId: number,
    title: string,
    description: string,
    pageRoute: string,
    environment: string,
    attachments?: File[]
  ) => {
    // Forward to JSM creation with minimal data
    return this.createJSMTicket({
      categoryId,
      title,
      description,
      pageRoute,
      environment,
      attachments,
    });
  };

  /**
   * Fetch all tickets for the current user
   */
  fetchMyTickets = async (searchQuery?: string) => {
    this.isTicketsLoading = true;
    try {
      const data = await supportTicketServices.getMyTickets(searchQuery);
      this.myTickets = data.tickets;
    } catch (error) {
      console.log('SupportTicketStore => fetchMyTickets => ', error);
      toast.error('Failed to load tickets');
    } finally {
      this.isTicketsLoading = false;
    }
  };

  /**
   * Fetch detailed information for a specific ticket
   */
  fetchTicketDetail = async (ticketId: string) => {
    this.isTicketDetailLoading = true;
    try {
      const data = await supportTicketServices.getTicketDetail(ticketId);
      this.selectedTicket = data.ticket;
    } catch (error) {
      console.log('SupportTicketStore => fetchTicketDetail => ', error);
      toast.error('Failed to load ticket details');
    } finally {
      this.isTicketDetailLoading = false;
    }
  };

  /**
   * Sync ticket status from Jira
   */
  syncTicketStatus = async (ticketId: string) => {
    this.isSyncingStatus = true;
    try {
      const data = await supportTicketServices.syncTicketStatus(ticketId);
      toast.success('Status synced successfully');

      // Refresh the ticket list and detail if viewing
      this.fetchMyTickets();
      if (this.selectedTicket && this.selectedTicket.jiraTicketId === ticketId) {
        this.fetchTicketDetail(ticketId);
      }

      return data;
    } catch (error) {
      console.log('SupportTicketStore => syncTicketStatus => ', error);
      toast.error('Failed to sync status');
      throw error;
    } finally {
      this.isSyncingStatus = false;
    }
  };

  /**
   * Open create ticket modal and fetch categories
   */
  openCreateModal = () => {
    this.setCreateModalVisible(true);
    if (this.categories.length === 0) {
      this.fetchCategories();
    }
  };

  /**
   * Open ticket detail modal and fetch ticket details
   */
  openDetailModal = (ticketId: string) => {
    this.setDetailModalVisible(true);
    this.fetchTicketDetail(ticketId);
  };

  /**
   * Set create modal visibility
   */
  setCreateModalVisible = (visible: boolean) => {
    this.isCreateModalVisible = visible;
  };

  /**
   * Set detail modal visibility
   */
  setDetailModalVisible = (visible: boolean) => {
    this.isDetailModalVisible = visible;
    // Clear selected ticket when closing
    if (!visible) {
      this.selectedTicket = null;
    }
  };

  // ==================== CHAT METHODS ====================

  /**
   * Open chat - restore previous state or start fresh
   */
  openChat = () => {
    this.isChatOpen = true;

    // Restore conversation from where user left off
    if (this.chatStep === ChatStep.CLOSED || this.chatStep === ChatStep.SUBMITTED) {
      this.chatStep = ChatStep.WELCOME;
      this.chatMessages = [];
      this.addWelcomeMessage();
    }
    // Otherwise, continue from current step

    if (this.categories.length === 0) {
      this.fetchCategories();
    }
  };

  /**
   * Close chat - preserve state (DO NOT RESET)
   */
  closeChat = () => {
    this.isChatOpen = false;
    // Keep chatStep, chatMessages, and formData intact for state persistence
  };

  /**
   * Reset conversation - only called after successful submission or explicit user action
   */
  resetChat = () => {
    this.chatStep = ChatStep.CLOSED;
    this.chatMessages = [];
    this.formData = {
      selectedCategory: null,
      title: '',
      description: '',
      attachments: []
    };
  };

  /**
   * Add message to conversation
   */
  addMessage = (message: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    this.chatMessages.push({
      ...message,
      id: uuidv4(),
      timestamp: new Date()
    });
  };

  /**
   * Add welcome message
   */
  addWelcomeMessage = () => {
    this.addMessage({
      type: 'bot',
      content: "Hi! I'm here to help you report an issue. 👋",
      metadata: { step: ChatStep.WELCOME }
    });

    setTimeout(() => {
      this.addMessage({
        type: 'bot',
        content: "Let's get started - what type of issue are you experiencing?",
        metadata: { step: ChatStep.WELCOME }
      });
      this.chatStep = ChatStep.CATEGORY;
    }, 500);
  };

  /**
   * Show typing indicator with delay
   */
  showTypingIndicator = async (delayMs: number = 800): Promise<void> => {
    this.isTypingIndicatorVisible = true;
    await new Promise(resolve => setTimeout(resolve, delayMs));
    this.isTypingIndicatorVisible = false;
  };

  /**
   * Handle category selection
   */
  handleCategorySelect = async (category: TicketCategory) => {
    this.formData.selectedCategory = category;
    this.addMessage({
      type: 'user',
      content: category.category_name,
      metadata: { step: ChatStep.CATEGORY }
    });

    await this.showTypingIndicator();

    this.addMessage({
      type: 'bot',
      content: "Got it! Please provide a brief title for your issue.",
      metadata: { step: ChatStep.TITLE }
    });

    this.chatStep = ChatStep.TITLE;
  };

  /**
   * Handle title submission
   */
  handleTitleSubmit = async (title: string) => {
    if (!title || title.trim().length === 0) {
      this.addMessage({
        type: 'bot',
        content: "Please provide a title for your issue.",
        metadata: { step: ChatStep.TITLE, isError: true }
      });
      return;
    }

    if (title.length > 255) {
      this.addMessage({
        type: 'bot',
        content: "Title must be less than 255 characters. Please try again.",
        metadata: { step: ChatStep.TITLE, isError: true }
      });
      return;
    }

    this.formData.title = title;
    this.addMessage({
      type: 'user',
      content: title,
      metadata: { step: ChatStep.TITLE }
    });

    await this.showTypingIndicator();

    this.addMessage({
      type: 'bot',
      content: "Thanks! Can you describe the issue in more detail?",
      metadata: { step: ChatStep.DESCRIPTION }
    });

    setTimeout(() => {
      this.addMessage({
        type: 'bot',
        content: "Include any steps to reproduce or error messages you see.",
        metadata: { step: ChatStep.DESCRIPTION }
      });
    }, 400);

    this.chatStep = ChatStep.DESCRIPTION;
  };

  /**
   * Handle description submission
   */
  handleDescriptionSubmit = async (description: string) => {
    if (!description || description.trim().length === 0) {
      this.addMessage({
        type: 'bot',
        content: "Please provide a description of your issue.",
        metadata: { step: ChatStep.DESCRIPTION, isError: true }
      });
      return;
    }

    if (description.length > 2000) {
      this.addMessage({
        type: 'bot',
        content: "Description must be less than 2000 characters. Please try again.",
        metadata: { step: ChatStep.DESCRIPTION, isError: true }
      });
      return;
    }

    this.formData.description = description;
    this.addMessage({
      type: 'user',
      content: description,
      metadata: { step: ChatStep.DESCRIPTION }
    });

    await this.showTypingIndicator();

    this.addMessage({
      type: 'bot',
      content: "Perfect! Would you like to attach any screenshots or files?",
      metadata: { step: ChatStep.ATTACHMENTS }
    });

    setTimeout(() => {
      this.addMessage({
        type: 'bot',
        content: "This is optional - you can skip if not needed.",
        metadata: { step: ChatStep.ATTACHMENTS }
      });
    }, 400);

    this.chatStep = ChatStep.ATTACHMENTS;
  };

  /**
   * Handle attachments submission
   */
  handleAttachmentsSubmit = async (attachments: File[]) => {
    this.formData.attachments = attachments;

    if (attachments.length > 0) {
      this.addMessage({
        type: 'user',
        content: `${attachments.length} file(s) attached`,
        metadata: { step: ChatStep.ATTACHMENTS }
      });
    } else {
      this.addMessage({
        type: 'user',
        content: "Skip attachments",
        metadata: { step: ChatStep.ATTACHMENTS }
      });
    }

    await this.showTypingIndicator();

    this.addMessage({
      type: 'bot',
      content: "Great! Let me confirm the details:",
      metadata: { step: ChatStep.REVIEW }
    });

    this.chatStep = ChatStep.REVIEW;
  };

  /**
   * Submit chat ticket with comprehensive data
   */
  submitChatTicket = async () => {
    this.isCreatingTicket = true;

    // Show submitting toast
    const submittingToast = toast.info('Submitting ticket...', {
      autoClose: false,
      closeButton: false,
    });

    try {
      // Helper to get environment
      const getEnvironment = (): string => {
        const origin = window.location.origin;
        if (origin.includes('localhost')) return 'local';
        if (origin.includes('dev')) return 'dev';
        if (origin.includes('staging')) return 'staging';
        return 'production';
      };

      // Capture comprehensive data
      const { browserInfo, additionalContext } = await captureComprehensiveTicketData({
        'Page Title': document.title,
        'User Role': 'N/A', // Will be set by calling code if available
        'Session Active': 'Yes',
      }, false);

      // Create ticket
      const result = await this.createJSMTicket({
        categoryId: this.formData.selectedCategory!.id,
        title: this.formData.title,
        description: this.formData.description,
        pageRoute: window.location.pathname,
        environment: getEnvironment(),
        priority: this.formData.selectedCategory?.category_code === 'UI_BUG' ? 'High' : 'Medium',
        pageUrl: window.location.href,
        screenResolution: `${screen.width}x${screen.height}`,
        sessionId: sessionStorage.getItem('sessionId') || undefined,
        userRole: undefined,
        browserInfo,
        additionalContext,
        attachments: this.formData.attachments.length > 0 ? this.formData.attachments : undefined,
      });

      // Dismiss submitting toast
      toast.dismiss(submittingToast);

      // Show success in chat
      this.addMessage({
        type: 'bot',
        content: `✓ Ticket created successfully!\n\nTicket ID: ${result.ticket.jiraTicketId}`,
        metadata: { step: ChatStep.SUBMITTED }
      });

      this.chatStep = ChatStep.SUBMITTED;

      // Show success toast with Jira link
      if (result.ticket.jiraUrl) {
        toast.success(
          `Ticket ${result.ticket.jiraTicketId} created! Click to view in Jira.`,
          {
            onClick: () => window.open(result.ticket.jiraUrl, '_blank'),
            autoClose: 8000
          }
        );
      }

      // Auto-close and reset after 5 seconds
      setTimeout(() => {
        this.closeChat();
        this.resetChat();
      }, 5000);

      return result;
    } catch (error) {
      toast.dismiss(submittingToast);
      this.addMessage({
        type: 'bot',
        content: 'Sorry, there was an error creating your ticket. Please try again.',
        metadata: { step: this.chatStep, isError: true }
      });
      throw error;
    } finally {
      this.isCreatingTicket = false;
    }
  };

  // ==================== END CHAT METHODS ====================

  /**
   * Reset store to initial state
   */
  resetStore = () => {
    this.categories = [];
    this.myTickets = [];
    this.selectedTicket = null;
    this.isCreateModalVisible = false;
    this.isDetailModalVisible = false;
    this.isCategoriesLoading = false;
    this.isTicketsLoading = false;
    this.isTicketDetailLoading = false;
    this.isCreatingTicket = false;
    this.isSyncingStatus = false;

    // Reset chat state
    this.isChatOpen = false;
    this.chatStep = ChatStep.CLOSED;
    this.chatMessages = [];
    this.isTypingIndicatorVisible = false;
    this.formData = {
      selectedCategory: null,
      title: '',
      description: '',
      attachments: []
    };
  };
}
